import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Menu, X, Settings } from 'lucide-react';
import ServiceManager from '../ServiceManager';
import PackManager from '../PackManager';
import PromotionManager from '../PromotionManager';
import WheelManager from '../WheelManager';
import AppointmentManager from '../AppointmentManager';
import DiscussionManager from '../DiscussionManager';
import ServicesPageConfig from '../ServicesPageConfig';
import { serviceApiService, Service } from '../../services/serviceApi';

interface MobileDashboardAdminProps {
  onClose: () => void;
}

type TabType = 'services' | 'packs' | 'promotions' | 'wheel' | 'appointments' | 'discussions' | 'servicespage';

const MobileDashboardAdmin: React.FC<MobileDashboardAdminProps> = ({ onClose }) => {
  const [activeTab, setActiveTab] = useState<TabType>('services');
  const [services, setServices] = useState<Service[]>([]);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  const tabs = [
    { id: 'services' as TabType, name: 'Services', icon: '📋', color: 'blue' },
    { id: 'packs' as TabType, name: 'Packs', icon: '📦', color: 'green' },
    { id: 'promotions' as TabType, name: 'Promotions', icon: '🎯', color: 'purple' },
    { id: 'wheel' as TabType, name: 'Roue de Promo', icon: '🎰', color: 'purple' },
    { id: 'appointments' as TabType, name: 'Rendez-vous', icon: '📅', color: 'orange' },
    { id: 'discussions' as TabType, name: 'Discussions', icon: '💬', color: 'pink' },
    { id: 'servicespage' as TabType, name: 'Config Services', icon: '🎨', color: 'indigo' },
  ];

  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    try {
      const servicesData = await serviceApiService.getServices();
      setServices(Array.isArray(servicesData) ? servicesData : []);
    } catch (err) {
      console.error('Erreur chargement services:', err);
    }
  };

  const getCurrentTab = () => tabs.find(tab => tab.id === activeTab);
  const currentTab = getCurrentTab();

  const handleTabChange = (tabId: TabType) => {
    setActiveTab(tabId);
    setShowMobileMenu(false);
  };

  return (
    <div className="fixed inset-0 bg-gray-50 z-50 overflow-hidden">
      {/* Header Mobile */}
      <div className="bg-gradient-to-r from-amber-600 to-amber-700 shadow-lg">
        <div className="flex items-center justify-between p-4">
          <button
            onClick={onClose}
            className="flex items-center gap-2 text-amber-100 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm font-medium">Retour</span>
          </button>

          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-amber-500 rounded-lg flex items-center justify-center">
              <Settings className="w-4 h-4 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">Admin</h1>
            </div>
          </div>

          <button
            onClick={() => setShowMobileMenu(!showMobileMenu)}
            className="p-2 text-amber-100 hover:text-white transition-colors"
          >
            {showMobileMenu ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

      </div>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {showMobileMenu && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-40"
              onClick={() => setShowMobileMenu(false)}
            />
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="absolute top-20 left-4 right-4 bg-white rounded-2xl shadow-2xl border border-amber-200/50 z-50 overflow-hidden"
            >
              <div className="p-2">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all duration-200 ${
                      activeTab === tab.id
                        ? 'bg-amber-50 text-amber-700 border border-amber-200'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <span className="text-xl">{tab.icon}</span>
                    <span className="font-medium">{tab.name}</span>
                    {activeTab === tab.id && (
                      <div className="ml-auto w-2 h-2 bg-amber-500 rounded-full" />
                    )}
                  </button>
                ))}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Content Area */}
      <div className="flex-1 overflow-y-auto" style={{ height: 'calc(100vh - 80px)' }}>
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.2 }}
          className="min-h-full"
        >
          {/* Content Header */}
          <div className="bg-white border-b border-gray-200 px-4 py-4">
            <div className="flex items-center gap-3 mobile-keep-row">
              <div className={`w-10 h-10 bg-${currentTab?.color}-100 rounded-xl flex items-center justify-center`}>
                <span className="text-xl">{currentTab?.icon}</span>
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">{currentTab?.name}</h2>
                <p className="text-sm text-gray-600">Gestion et configuration</p>
              </div>
            </div>
          </div>

          {/* Tab Content */}
          <div className="bg-gray-50 min-h-full">
            {activeTab === 'services' && (
              <div className="mobile-admin-content mobile-services-wrapper">
                <ServiceManager />
              </div>
            )}
            {activeTab === 'packs' && (
              <div className="mobile-admin-content mobile-packs-wrapper">
                <PackManager services={services} />
              </div>
            )}
            {activeTab === 'promotions' && (
              <div className="mobile-admin-content mobile-promotions-wrapper">
                <PromotionManager onClose={() => {}} />
              </div>
            )}
            {activeTab === 'wheel' && (
              <div className="mobile-admin-content mobile-wheel-wrapper">
                <WheelManager />
              </div>
            )}
            {activeTab === 'appointments' && (
              <div className="mobile-admin-content mobile-appointments-wrapper">
                <AppointmentManager />
              </div>
            )}
            {activeTab === 'discussions' && (
              <div className="mobile-admin-content mobile-discussions-wrapper">
                <DiscussionManager />
              </div>
            )}
            {activeTab === 'servicespage' && (
              <div className="mobile-admin-content mobile-servicespage-wrapper">
                <ServicesPageConfig />
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Styles pour optimiser les composants existants sur mobile */}
      <style>{`
        /* === OPTIMISATIONS GÉNÉRALES MOBILE === */
        .mobile-admin-content {
          font-size: 14px;
          line-height: 1.4;
        }
        
        /* Réduction des paddings */
        .mobile-admin-content .p-6 {
          padding: 0.75rem !important;
        }
        
        .mobile-admin-content .p-8 {
          padding: 1rem !important;
        }
        
        .mobile-admin-content .p-4 {
          padding: 0.5rem !important;
        }
        
        .mobile-admin-content .px-6 {
          padding-left: 0.75rem !important;
          padding-right: 0.75rem !important;
        }
        
        .mobile-admin-content .py-6 {
          padding-top: 0.75rem !important;
          padding-bottom: 0.75rem !important;
        }
        
        /* Réduction des marges */
        .mobile-admin-content .mb-6 {
          margin-bottom: 1rem !important;
        }
        
        .mobile-admin-content .mb-4 {
          margin-bottom: 0.75rem !important;
        }
        
        .mobile-admin-content .space-x-4 > * + * {
          margin-left: 0.5rem !important;
        }
        
        .mobile-admin-content .space-y-6 > * + * {
          margin-top: 1rem !important;
        }
        
        .mobile-admin-content .space-x-2 > * + * {
          margin-left: 0.25rem !important;
        }
        
        /* === GRILLES ET LAYOUTS === */
        .mobile-admin-content .grid-cols-4 {
          grid-template-columns: 1fr 1fr !important;
          gap: 0.5rem !important;
        }
        
        .mobile-admin-content .grid-cols-3 {
          grid-template-columns: 1fr !important;
          gap: 0.5rem !important;
        }
        
        .mobile-admin-content .grid-cols-2 {
          grid-template-columns: 1fr !important;
          gap: 0.5rem !important;
        }
        
        .mobile-admin-content .gap-4 {
          gap: 0.5rem !important;
        }
        
        .mobile-admin-content .gap-6 {
          gap: 0.75rem !important;
        }
        
        /* === FLEXBOX OPTIMISATIONS === */
        .mobile-admin-content .flex.items-center.justify-between {
          flex-direction: column !important;
          align-items: flex-start !important;
          gap: 0.5rem !important;
        }
        
        .mobile-admin-content .flex.items-center.justify-between.mobile-keep-row {
          flex-direction: row !important;
          align-items: center !important;
        }
        
        .mobile-admin-content .flex.space-x-2 {
          flex-wrap: wrap !important;
          gap: 0.25rem !important;
        }
        
        .mobile-admin-content .flex.space-x-4 {
          flex-wrap: wrap !important;
          gap: 0.5rem !important;
        }
        
        /* === TEXTES === */
        .mobile-admin-content .text-xl {
          font-size: 1.125rem !important;
        }
        
        .mobile-admin-content .text-2xl {
          font-size: 1.25rem !important;
        }
        
        .mobile-admin-content .text-3xl {
          font-size: 1.375rem !important;
        }
        
        .mobile-admin-content h1 {
          font-size: 1.25rem !important;
        }
        
        .mobile-admin-content h2 {
          font-size: 1.125rem !important;
        }
        
        .mobile-admin-content h3 {
          font-size: 1rem !important;
        }
        
        /* === BOUTONS === */
        .mobile-admin-content button {
          min-height: 44px !important;
          padding: 0.5rem 0.75rem !important;
          font-size: 14px !important;
          white-space: nowrap;
        }
        
        .mobile-admin-content button.px-4 {
          padding-left: 0.75rem !important;
          padding-right: 0.75rem !important;
        }
        
        .mobile-admin-content button.py-2 {
          padding-top: 0.5rem !important;
          padding-bottom: 0.5rem !important;
        }
        
        /* Boutons avec texte long */
        .mobile-admin-content button span {
          font-size: 12px !important;
        }
        
        /* === FORMULAIRES === */
        .mobile-admin-content input,
        .mobile-admin-content textarea,
        .mobile-admin-content select {
          font-size: 16px !important;
          min-height: 44px !important;
          padding: 0.5rem !important;
        }
        
        .mobile-admin-content .form-group {
          margin-bottom: 0.75rem !important;
        }
        
        /* === TABLEAUX === */
        .mobile-admin-content table {
          font-size: 12px !important;
          width: 100% !important;
        }
        
        .mobile-admin-content th,
        .mobile-admin-content td {
          padding: 0.25rem !important;
          text-align: left !important;
        }
        
        .mobile-admin-content .overflow-x-auto {
          -webkit-overflow-scrolling: touch !important;
        }
        
        /* Masquer certaines colonnes sur mobile */
        .mobile-admin-content .hidden-mobile {
          display: none !important;
        }
        
        /* === CARTES ET CONTENEURS === */
        .mobile-admin-content .rounded-lg {
          border-radius: 0.5rem !important;
        }
        
        .mobile-admin-content .border {
          border-width: 1px !important;
        }
        
        /* === MODALES === */
        .mobile-admin-content .fixed.inset-0 {
          padding: 0.5rem !important;
        }
        
        .mobile-admin-content .modal-content {
          margin: 0.5rem !important;
          max-height: calc(100vh - 1rem) !important;
          overflow-y: auto !important;
          border-radius: 1rem !important;
        }
        
        /* === STATISTIQUES === */
        .mobile-admin-content .bg-blue-50,
        .mobile-admin-content .bg-green-50,
        .mobile-admin-content .bg-red-50,
        .mobile-admin-content .bg-purple-50 {
          padding: 0.75rem !important;
          margin-bottom: 0.5rem !important;
        }
        
        .mobile-admin-content .bg-blue-50 .text-2xl,
        .mobile-admin-content .bg-green-50 .text-2xl,
        .mobile-admin-content .bg-red-50 .text-2xl,
        .mobile-admin-content .bg-purple-50 .text-2xl {
          font-size: 1.125rem !important;
        }
        
        /* === NAVIGATION TABS === */
        .mobile-admin-content nav.flex {
          flex-direction: column !important;
          gap: 0.25rem !important;
        }
        
        .mobile-admin-content nav button {
          width: 100% !important;
          justify-content: flex-start !important;
          padding: 0.75rem !important;
          border-radius: 0.5rem !important;
        }
        
        /* === LISTES === */
        .mobile-admin-content .divide-y > * {
          padding: 0.75rem !important;
          border-bottom: 1px solid #e5e7eb !important;
        }
        
        /* === RESPONSIVE UTILITIES === */
        .mobile-admin-content .w-full-mobile {
          width: 100% !important;
        }
        
        .mobile-admin-content .flex-col-mobile {
          flex-direction: column !important;
        }
        
        .mobile-admin-content .text-center-mobile {
          text-align: center !important;
        }
        
        /* === SCROLLING === */
        .mobile-admin-content {
          -webkit-overflow-scrolling: touch;
        }
        
        .mobile-admin-content .overflow-y-auto {
          -webkit-overflow-scrolling: touch !important;
        }
        
        /* === ANIMATIONS === */
        .mobile-admin-content .transition-all {
          transition: all 0.2s ease !important;
        }
        
        /* === SPÉCIFIQUE AUX COMPOSANTS === */
        
        /* ServiceManager */
        .mobile-admin-content .grid.grid-cols-1.md\\:grid-cols-4 {
          grid-template-columns: 1fr 1fr !important;
          gap: 0.5rem !important;
        }
        
        /* PackManager */
        .mobile-admin-content .pack-card {
          margin-bottom: 0.75rem !important;
        }
        
        /* PromotionManager */
        .mobile-admin-content .promotion-form {
          padding: 0.75rem !important;
        }
        
        /* WheelManager */
        .mobile-admin-content .wheel-config {
          padding: 0.75rem !important;
        }
        
        /* AppointmentManager */
        .mobile-admin-content .appointment-list {
          padding: 0.75rem !important;
        }
        
        /* DiscussionManager */
        .mobile-admin-content .discussion-item {
          padding: 0.75rem !important;
          margin-bottom: 0.5rem !important;
        }
        
        /* === CORRECTIONS SPÉCIFIQUES === */
        
        /* Éviter le débordement horizontal */
        .mobile-admin-content * {
          max-width: 100% !important;
          box-sizing: border-box !important;
        }
        
        /* Textes longs */
        .mobile-admin-content .text-sm {
          font-size: 12px !important;
          line-height: 1.3 !important;
        }
        
        /* Images et icônes */
        .mobile-admin-content .w-10.h-10 {
          width: 2rem !important;
          height: 2rem !important;
        }
        
        .mobile-admin-content .w-16.h-16 {
          width: 3rem !important;
          height: 3rem !important;
        }
        
        /* Badges et labels */
        .mobile-admin-content .badge {
          font-size: 11px !important;
          padding: 0.25rem 0.5rem !important;
        }
        
        /* === CORRECTIONS DE DÉBORDEMENT === */
        .mobile-admin-content .overflow-hidden {
          overflow: hidden !important;
        }
        
        .mobile-admin-content .truncate {
          overflow: hidden !important;
          text-overflow: ellipsis !important;
          white-space: nowrap !important;
        }
        
        /* Force le wrap pour les éléments flex */
        .mobile-admin-content .flex {
          flex-wrap: wrap !important;
        }
        
        .mobile-admin-content .flex.no-wrap {
          flex-wrap: nowrap !important;
        }
        
        /* === WRAPPERS SPÉCIFIQUES PAR COMPOSANT === */
        
        /* ServiceManager Wrapper */
        .mobile-services-wrapper {
          padding: 0 !important;
        }
        
        .mobile-services-wrapper .grid.grid-cols-1.md\\:grid-cols-4 {
          grid-template-columns: 1fr 1fr !important;
          gap: 0.5rem !important;
        }
        
        .mobile-services-wrapper .flex.items-center.justify-between {
          flex-direction: column !important;
          align-items: stretch !important;
          gap: 0.75rem !important;
        }
        
        .mobile-services-wrapper button {
          width: 100% !important;
          justify-content: center !important;
        }
        
        /* Boutons d'action des services - garder sur une ligne */
        .mobile-services-wrapper .flex.space-x-2 {
          flex-wrap: nowrap !important;
          justify-content: center !important;
          gap: 0.25rem !important;
        }
        
        .mobile-services-wrapper .flex.space-x-2 button {
          width: auto !important;
          flex: 1 !important;
          min-width: 44px !important;
          padding: 0.5rem !important;
        }
        
        /* PackManager Wrapper */
        .mobile-packs-wrapper {
          padding: 0.75rem !important;
        }
        
        .mobile-packs-wrapper .grid {
          grid-template-columns: 1fr !important;
          gap: 0.75rem !important;
        }
        
        .mobile-packs-wrapper .flex.items-center.justify-between {
          flex-direction: column !important;
          align-items: stretch !important;
          gap: 0.5rem !important;
        }
        
        /* PromotionManager Wrapper */
        .mobile-promotions-wrapper {
          padding: 0.75rem !important;
        }
        
        .mobile-promotions-wrapper .grid {
          grid-template-columns: 1fr !important;
        }
        
        .mobile-promotions-wrapper .flex.space-x-4 {
          flex-direction: column !important;
          gap: 0.5rem !important;
        }
        
        /* WheelManager Wrapper */
        .mobile-wheel-wrapper {
          padding: 0.75rem !important;
        }
        
        .mobile-wheel-wrapper .grid {
          grid-template-columns: 1fr !important;
        }
        
        /* AppointmentManager Wrapper */
        .mobile-appointments-wrapper {
          padding: 0.75rem !important;
        }
        
        .mobile-appointments-wrapper .grid {
          grid-template-columns: 1fr !important;
        }
        
        .mobile-appointments-wrapper table {
          display: block !important;
          overflow-x: auto !important;
          white-space: nowrap !important;
        }
        
        /* DiscussionManager Wrapper */
        .mobile-discussions-wrapper {
          padding: 0.75rem !important;
        }
        
        .mobile-discussions-wrapper .grid {
          grid-template-columns: 1fr !important;
        }
        
        /* ServicesPageConfig Wrapper */
        .mobile-servicespage-wrapper {
          padding: 0.75rem !important;
        }
        
        .mobile-servicespage-wrapper .grid {
          grid-template-columns: 1fr !important;
        }
        
        /* Masquer les éléments de debug et boutons de test */
        .mobile-servicespage-wrapper .bg-yellow-50,
        .mobile-servicespage-wrapper .bg-red-50 {
          display: none !important;
        }
        
        /* Masquer les boutons de debug par couleur de fond */
        .mobile-servicespage-wrapper .bg-blue-500,
        .mobile-servicespage-wrapper .bg-green-500,
        .mobile-servicespage-wrapper .bg-purple-500,
        .mobile-servicespage-wrapper .bg-orange-500 {
          display: none !important;
        }
        
        /* Masquer les divs de debug spécifiques */
        .mobile-servicespage-wrapper div[class*="bg-red-50"],
        .mobile-servicespage-wrapper div[class*="bg-yellow-50"] {
          display: none !important;
        }
        
        /* === CORRECTIONS SUPPLÉMENTAIRES === */
        
        /* Forcer la largeur maximale pour éviter les débordements */
        .mobile-admin-content .container,
        .mobile-admin-content .max-w-7xl,
        .mobile-admin-content .max-w-6xl,
        .mobile-admin-content .max-w-5xl,
        .mobile-admin-content .max-w-4xl {
          max-width: 100% !important;
          padding-left: 0.75rem !important;
          padding-right: 0.75rem !important;
        }
        
        /* Ajustements pour les boutons d'action */
        .mobile-admin-content .flex.items-center.space-x-2 button {
          flex: 1 !important;
          min-width: 0 !important;
        }
        
        .mobile-admin-content .flex.items-center.space-x-2 button span {
          overflow: hidden !important;
          text-overflow: ellipsis !important;
        }
        
        /* Ajustements pour les formulaires */
        .mobile-admin-content .form-grid {
          display: grid !important;
          grid-template-columns: 1fr !important;
          gap: 0.75rem !important;
        }
        
        /* Ajustements pour les modales */
        .mobile-admin-content .modal {
          position: fixed !important;
          top: 0 !important;
          left: 0 !important;
          right: 0 !important;
          bottom: 0 !important;
          z-index: 9999 !important;
          padding: 1rem !important;
        }
        
        .mobile-admin-content .modal-dialog {
          width: 100% !important;
          max-width: 100% !important;
          margin: 0 !important;
          height: 100% !important;
          display: flex !important;
          flex-direction: column !important;
        }
        
        .mobile-admin-content .modal-content {
          flex: 1 !important;
          overflow-y: auto !important;
          border-radius: 1rem !important;
        }
        
        /* Corrections pour les tableaux */
        .mobile-admin-content .table-responsive {
          overflow-x: auto !important;
          -webkit-overflow-scrolling: touch !important;
        }
        
        .mobile-admin-content table td:first-child,
        .mobile-admin-content table th:first-child {
          position: sticky !important;
          left: 0 !important;
          background: white !important;
          z-index: 1 !important;
        }
        
        /* Corrections pour les cartes */
        .mobile-admin-content .card {
          margin-bottom: 0.75rem !important;
          border-radius: 0.75rem !important;
        }
        
        .mobile-admin-content .card-body {
          padding: 0.75rem !important;
        }
        
        /* Corrections pour les alertes */
        .mobile-admin-content .alert {
          padding: 0.75rem !important;
          margin-bottom: 0.75rem !important;
          border-radius: 0.5rem !important;
          font-size: 14px !important;
        }
      `}</style>
    </div>
  );
};

export default MobileDashboardAdmin;
